import app from '@app/app';
import { nextTick } from 'vue';
import Store from '@app/store';
import { createRouter, createWebHistory } from 'vue-router';
import RouterFunctions from '@app/router/functions';
import { EventTracking, EventType } from '@app/tracking';
import SessionExpired from '@app/exceptions/SessionExpired';
const routeable = window.PRELOADED_PAGE_DATA;
const initialRoutes = [];
/*
 * Convert and push initial server-side rendered URI to router
 */
if (routeable) {
    const initRoute = RouterFunctions.makeRoute(routeable.route);
    initialRoutes.push(initRoute);
    Store.MUTATIONS.setPageData(initRoute.path, routeable);
}
/*
 * Main Vue Router Setup
 */
const router = createRouter({
    history: createWebHistory(),
    routes: initialRoutes,
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return { el: to.hash, behavior: 'smooth' };
        }
        else if (savedPosition) {
            return savedPosition;
        }
        else {
            return { top: 0, left: 0 };
        }
    }
});
/**
 * Router Hooks before a route change
 */
router.beforeEach((to, from, next) => {
    // Update page metadata
    if (to.meta) {
        RouterFunctions.updateMedadata(to);
    }
    // Ignore routes which dont have backend page data
    if (!to.meta.hasPageData) {
        return next();
    }
    if (Store.STATE.pageData[to.path]) {
        // Immediate navigation because page data already exists
        next();
        // Still make API call to update in the background (unless prefetched)
        if (typeof Store.STATE.pageData[to.path].__prefetched__ === 'undefined' && !window.IS_FIRST_PAGE_LOAD) {
            Store.ACTIONS.loadPageData(to.path);
        }
    }
    else if (RouterFunctions.routeExists(to.path)) {
        // Load page data and navigate on completion
        Store.MUTATIONS.setLoading(true);
        Store.ACTIONS.loadPageData(to.path).then(data => {
            next();
        }).catch(error => {
            // Hard redirect if Laravel session expired (CSRF protection)
            if (error instanceof SessionExpired) {
                window.location.href = to.path;
                return;
            }
        });
    }
    else {
        next();
    }
});
/**
 * Router Hooks after a route change
 */
router.afterEach((to, from) => {
    // Scroll to anchor if first pageload has anchor
    // Stop loading state after scrolling or immediately on normal route changes
    if (to.hash && from.name === null) {
        nextTick(() => {
            setTimeout(() => {
                RouterFunctions.scrollToAnchor(window.location.hash);
                Store.MUTATIONS.setLoading(false);
            }, app.config.globalProperties.$pageTransitionDuration);
        });
    }
    else {
        Store.MUTATIONS.setLoading(false);
    }
    // Cancel further process if only hash changed
    if (to.path === from.path && to.hash != from.hash) {
        return;
    }
    // Dispatch a page view event for tracking services
    EventTracking.dispatch(EventType.PageView, {
        title: to.meta.title,
        path: to.path,
        isFirstPageLoad: window.IS_FIRST_PAGE_LOAD
    });
    // Unset first pageload flag
    if (window.IS_FIRST_PAGE_LOAD) {
        window.IS_FIRST_PAGE_LOAD = false;
    }
    // Remove SSR layout if exists
    nextTick(() => {
        const ssrLayout = document.getElementById('ssr');
        if (ssrLayout) {
            ssrLayout.classList.add('ssr-hidden');
            setTimeout(() => ssrLayout.parentNode.removeChild(ssrLayout), 350);
        }
    });
});
export default router;
